import { isEdelvives } from 'app/utils/utils';
import { FormField, TextArea } from 'ui';
import * as coreEntities from 'core/store/index';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import SelectLearningObjectives from '../SelectLearningObjectives/SelectLearningObjectives';
import SelectLearningObjectivesTanAI from '../SelectLearningObjectivesTanAI/SelectLearningObjectivesTanAI';
import './_evaluation-criteria-selector.scss';

/**
 * EvaluationCriteriaSelector - Composite component for selecting evaluation criteria
 *
 * Shows learning objectives tree selector + textarea if learning objectives exist,
 * or only textarea if no learning objectives are available.
 *
 * @param {Object} props - Component props
 * @returns {JSX.Element} Rendered component
 */
function EvaluationCriteriaSelector({ disciplineId, educationLevel, educationYear, onChange, required, value }) {
  const { t } = useTranslation(['chatbot', 'contents']);
  const dispatch = useDispatch();

  // Temporarily disabled - will be re-enabled later
  const hasLearningObjectives = false;

  // Fetch learning objectives only when the legacy Tangerine flow is enabled.
  // For Edelvives we use a different selector (SelectLearningObjectivesTanAI) that
  // fetches its own data, so we skip this dispatch to avoid an unnecessary request.
  useEffect(() => {
    if (!hasLearningObjectives || isEdelvives()) return;
    dispatch(coreEntities.learningObjectives.actions.getLearningObjectives());
  }, [dispatch, hasLearningObjectives]);

  // Handle selected learning objectives change
  const handleSelectedChange = (selectedArray) => {
    onChange({
      ...value,
      selected: selectedArray,
    });
  };

  // Handle additional criteria text change
  const handleAdditionalChange = (e) => {
    onChange({
      ...value,
      additional: e.currentTarget.value,
    });
  };

  // Edelvives flow: official evaluation criteria selector + additional textarea
  if (isEdelvives()) {
    return (
      <div className="evaluation-criteria-selector">
        <div className="evaluation-criteria-selector__field">
          <label className="evaluation-criteria-selector__label label">{t('chatbot:teacher_tools_evaluation_criteria_label')}</label>
          <SelectLearningObjectivesTanAI
            disciplineId={disciplineId}
            educationLevel={educationLevel}
            educationYear={educationYear}
            onChange={handleSelectedChange}
            value={value?.selected || []}
          />
        </div>

        <div className="evaluation-criteria-selector__field">
          <FormField label={t('chatbot:teacher_tools_additional_criteria_label')}>
            <TextArea
              name="additionalCriteria"
              onChange={handleAdditionalChange}
              placeholder={t('chatbot:teacher_tools_additional_criteria_placeholder')}
              rows={3}
              value={value?.additional || ''}
            />
          </FormField>
        </div>
      </div>
    );
  }

  // Legacy Tangerine flow (currently disabled via hasLearningObjectives = false)
  if (hasLearningObjectives) {
    return (
      <div className="evaluation-criteria-selector">
        <div className="evaluation-criteria-selector__field">
          <label className="evaluation-criteria-selector__label label">{t('chatbot:teacher_tools_evaluation_criteria_label')}</label>
          <SelectLearningObjectives
            onChange={handleSelectedChange}
            placeholder={t('chatbot:teacher_tools_evaluation_criteria_placeholder')}
            showBadge={false}
            value={value?.selected || []}
            variant="input"
          />
        </div>

        <div className="evaluation-criteria-selector__field">
          <FormField label={t('chatbot:teacher_tools_additional_criteria_label')}>
            <TextArea
              name="additionalCriteria"
              onChange={handleAdditionalChange}
              placeholder={t('chatbot:teacher_tools_additional_criteria_placeholder')}
              rows={3}
              value={value?.additional || ''}
            />
          </FormField>
        </div>
      </div>
    );
  }

  // Fallback: Only textarea when no learning objectives exist
  return (
    <div className="evaluation-criteria-selector">
      <FormField isRequired={required} label={t('chatbot:teacher_tools_evaluation_criteria_label')}>
        <TextArea
          name="evaluationCriteria"
          onChange={handleAdditionalChange}
          placeholder={t('chatbot:teacher_tools_evaluation_criteria_placeholder')}
          rows={3}
          value={value?.additional || ''}
        />
      </FormField>
    </div>
  );
}

EvaluationCriteriaSelector.propTypes = {
  disciplineId: PropTypes.string,
  educationLevel: PropTypes.string,
  educationYear: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  required: PropTypes.bool,
  value: PropTypes.shape({
    additional: PropTypes.string,
    selected: PropTypes.array,
  }),
};

EvaluationCriteriaSelector.defaultProps = {
  disciplineId: undefined,
  educationLevel: undefined,
  educationYear: undefined,
  required: false,
  value: { additional: '', selected: [] },
};

export default EvaluationCriteriaSelector;
